import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";

const stats = [
  { value: 30, suffix: "sec", label: "average install time" },
  { value: 99, suffix: "%", label: "uptime guaranteed" },
  { value: 100, prefix: "<", suffix: "ms", label: "detection speed" },
  { value: 14, suffix: "-day", label: "free trial" },
];

const CountUp = ({ target, suffix = "", prefix = "" }: { target: number; suffix?: string; prefix?: string }) => {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1500;
          const steps = 40;
          const increment = target / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
              setValue(target);
              clearInterval(timer);
            } else {
              setValue(current);
            }
          }, duration / steps);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  const display = target % 1 === 0 ? Math.round(value).toLocaleString() : value.toFixed(1);

  return (
    <span ref={ref}>
      {prefix}{display}{suffix}
    </span>
  );
};

const TrustBar = () => {
  return (
    <section className="relative border-t border-border/50 bg-card/30">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Stats */}
        <div className="flex items-center justify-center gap-4 md:gap-14 flex-wrap mb-4">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="text-center"
            >
              <div className="font-data text-lg md:text-2xl font-bold text-foreground tracking-wider">
                <CountUp target={stat.value} suffix={stat.suffix} prefix={stat.prefix || ""} />
              </div>
              <div className="text-[9px] font-mono text-muted-foreground mt-0.5">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* SOC 2 Badge */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="flex items-center justify-center"
        >
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-border/50 text-[9px] font-mono text-muted-foreground/60">
            <ShieldCheck className="w-3 h-3 text-primary/60" />
            SOC 2 Type II — In Progress
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default TrustBar;
